import React, { useState, useRef, useCallback } from "react";
import useSpeech from "./useSpeech";
import BingoGlobe from "./BingoGlobe";

// soundOption: 'none' | 'numbers' | 'numbers_phrases'
const PopOver3D = ({ number, onClose, spinDuration = 3000, reshow = false, soundOption = 'numbers_phrases' }) => {
  const [phase, setPhase] = useState(reshow ? "complete" : "spinning"); // spinning | drawing | complete
  const [displayNumber, setDisplayNumber] = useState(number);
  const hasComplimented = useRef(false);

  const onFinish = (spokenText) => {
    // If sound is disabled, close immediately after animation
    if (soundOption === 'none') {
      setTimeout(onClose, 500);
      return;
    }
    if (spokenText === `${displayNumber}`) {
      setTimeout(onClose, 500);
    } else {
      speak(`${displayNumber}`);
    }
  };

  const { speak } = useSpeech(onFinish);

  const sayComplimentaryText = useCallback(() => {
    // If sound is disabled, just close after delay
    if (soundOption === 'none') {
      setTimeout(onClose, 1500);
      return;
    }

    // If only numbers, just speak the number
    if (soundOption === 'numbers') {
      if (!hasComplimented.current) {
        speak(`${number}`);
        hasComplimented.current = true;
      }
      return;
    }

    // Full mode: numbers + phrases
    const staticCompliments = {
      1: "Começou o jogo",
      5: "Aperte o cinto",
      6: "Meia Duzia",
      9: "Pingo no pé",
      10: "Craque de bola",
      11: "Um atrás do outro",
      12: "Na veia",
      13: "Deu azar",
      16: "O leão está solto",
      17: "O bicho que pula",
      18: "Já pode dirigir",
      22: "Dois patinhos na lagoa",
      23: "Descendente dele",
      31: "Preparem os fogos",
      33: "Idade de Cristo",
      38: "Justiça de Goiás",
      51: "Uma boa ideia",
      55: "Dois patinhos idosos",
      66: "Um tapa atrás da orelha",
      69: "Um indo, outro voltando",
      75: "Terminou o jogo",
    };

    const randomCompliments = [
      "Tá na hora do chá",
      "Aquecendo a cadeira",
      "Fechou a conta",
      "Olha a galera!",
      "Foguetinho subindo",
      "Chave do cofre",
      "Bingo chegando",
      "Avisa o pessoal",
      "Agora vai",
      "Vai pro jogo!",
      "Na reta final",
      "A vitória vem!",
      "Última chance",
      "Olha o prêmio",
      "Quem chegou primeiro?",
      "No pulo do gato",
      "Apressa que tá acabando",
      "Bingo em andamento",
      "A sorte está no ar",
      "Vem pra vitória",
      "Comemoração a caminho",
      "Virando o jogo",
      "Vai ser festa",
      "Na contagem regressiva",
      "Quase lá!",
      "Todo mundo de olho",
      "Quem tá na boa?",
      "Bingo. Tô brincando kkkk",
    ];

    const compliment =
      staticCompliments[number] || randomCompliments[Math.floor(Math.random() * randomCompliments.length)];

    if (!hasComplimented.current) {
      speak(compliment);
      hasComplimented.current = true;
    }
  }, [number, speak, soundOption, onClose]);

  // Start the draw sequence
  // eslint-disable-next-line
  React.useEffect(() => {
    if (reshow) {
      sayComplimentaryText();
      return;
    }

    // After spinDuration, transition to drawing phase
    const timer = setTimeout(() => {
      setPhase("drawing");
    }, spinDuration);

    return () => clearTimeout(timer);
  }, []);

  // Called when the ball zoom animation completes
  const handleDrawComplete = useCallback(() => {
    setPhase("complete");
    sayComplimentaryText();
  }, [sayComplimentaryText]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        className="bg-white rounded-lg shadow-lg relative overflow-hidden"
        style={{ width: "90vw", height: "90vh" }}
      >
        <button 
          onClick={onClose} 
          className="absolute top-2 right-2 text-red-500 hover:text-red-700 z-10 text-2xl"
        >
          ✖
        </button>
        
        {/* Always show 3D globe - ball stays visible after animation */}
        <BingoGlobe 
          drawnNumber={number}
          isDrawing={phase === "drawing" || phase === "complete"}
          onDrawComplete={handleDrawComplete}
          fullScreen
        />
      </div>
    </div>
  );
};

export default PopOver3D;
